import React, { useState, useRef, useEffect, useCallback } from 'react';
import styled from 'styled-components';

function Icons({
  icons,
  onMouseDown,
  onDoubleClick,
  displayFocus,
  mouse,
  selecting,
  setSelectedIcons,
}) {
  const [iconsRect, setIconsRect] = useState([]);
  const measure = useCallback(rect => {
    setIconsRect(iconsRect => {
      const index = iconsRect.findIndex(r => r.id === rect.id);
      if (index === -1) return [...iconsRect, rect];

      const nextIconsRect = [...iconsRect];
      nextIconsRect[index] = rect;
      return nextIconsRect;
    });
  }, []);

  useEffect(() => {
    function resetMeasurements() {
      setIconsRect([]);
    }

    window.addEventListener('resize', resetMeasurements);
    window.addEventListener('orientationchange', resetMeasurements);
    return () => {
      window.removeEventListener('resize', resetMeasurements);
      window.removeEventListener('orientationchange', resetMeasurements);
    };
  }, []);
  useEffect(() => {
    if (!selecting) return;
    const sx = Math.min(selecting.x, mouse.docX);
    const sy = Math.min(selecting.y, mouse.docY);
    const sw = Math.abs(selecting.x - mouse.docX);
    const sh = Math.abs(selecting.y - mouse.docY);
    const selectedIds = iconsRect
      .filter(rect => {
        const { x, y, w, h } = rect;
        return x - sx < sw && sx - x < w && y - sy < sh && sy - y < h;
      })
      .map(icon => icon.id);
    setSelectedIcons(selectedIds);
  }, [iconsRect, setSelectedIcons, selecting, mouse.docX, mouse.docY]);
  return (
    <IconsContainer>
      {icons.map(icon => (
        <StyledIcon
          key={icon.id}
          {...icon}
          displayFocus={displayFocus}
          onMouseDown={onMouseDown}
          onDoubleClick={onDoubleClick}
          measure={measure}
        />
      ))}
    </IconsContainer>
  );
}

function Icon({
  title,
  onMouseDown,
  onDoubleClick,
  icon,
  className,
  id,
  component,
  measure,
}) {
  const ref = useRef(null);
  function _onMouseDown() {
    onMouseDown(id);
  }
  function _onDoubleClick() {
    onDoubleClick(component);
  }
  useEffect(() => {
    const target = ref.current;
    if (!target) return;
    const { left, top, width, height } = ref.current.getBoundingClientRect();
    const posX = left + window.scrollX;
    const posY = top + window.scrollY;
    measure({ id, x: posX, y: posY, w: width, h: height });
  }, [id, measure]);
  return (
    <div
      className={className}
      onMouseDown={_onMouseDown}
      onDoubleClick={_onDoubleClick}
      ref={ref}
    >
      <div className={`${className}__img__container`}>
        <img src={icon} alt={title} className={`${className}__img`} />
      </div>
      <div className={`${className}__text__container`}>
        <div className={`${className}__text`}>{title}</div>
      </div>
    </div>
  );
}

const IconsContainer = styled.div`
  position: absolute;
  top: clamp(16px, 5vh, 40px);
  left: clamp(12px, 4vw, 40px);
  right: 12px;
  bottom: calc(34px + env(safe-area-inset-bottom, 0px));
  display: flex;
  flex-flow: column wrap;
  align-content: flex-start;
  gap: 30px 18px;
  overflow: visible;

  @media (max-height: 500px), (max-width: 480px) {
    top: 10px;
    left: 10px;
    bottom: calc(32px + env(safe-area-inset-bottom, 0px));
    gap: 6px 12px;
  }
`;

const StyledIcon = styled(Icon)`
  width: 70px;
  min-height: 72px;
  display: flex;
  flex-direction: column;
  align-items: center;
  &__text__container {
    width: 100%;
    font-size: 10px;
    color: white;
    text-shadow: 0 1px 1px black;
    margin-top: 5px;
    display: flex;
    justify-content: center;

    &:before {
      content: '';
      display: block;
      flex-grow: 1;
    }
    &:after {
      content: '';
      display: block;
      flex-grow: 1;
    }
  }
  &__text {
    padding: 0 3px 2px;
    background-color: ${({ isFocus, displayFocus }) =>
      isFocus && displayFocus ? '#0b61ff' : 'transparent'};
    text-align: center;
    flex-shrink: 1;
  }
  &__img__container {
    width: 30px;
    height: 30px;
    filter: ${({ isFocus, displayFocus }) =>
      isFocus && displayFocus ? 'drop-shadow(0 0 blue)' : ''};
  }
  &__img {
    width: 30px;
    height: 30px;
    opacity: ${({ isFocus, displayFocus }) =>
      isFocus && displayFocus ? 0.5 : 1};
  }

  @media (max-height: 500px), (max-width: 480px) {
    width: 58px;
    min-height: 50px;

    &__text__container {
      font-size: 10px;
      line-height: 12px;
      margin-top: 4px;
    }

    &__img__container,
    &__img {
      width: 28px;
      height: 28px;
    }
  }
`;

export default Icons;
